import { IField, tryParseJSON } from '@eventr/common'
import {
    FileWithStatus,
    MobileConfirmDialog,
    TDialogStore,
    UploadFileField,
    useDialogStore,
} from '@eventr/common-storybook'
import { useSignal } from '@preact/signals-react'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { editItineraryFieldsTranslation } from '../../../i18n/translateHelpers'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { removePrefix } from '../../media/media.slice'
import { useEditSectionContext } from '../editSectionProvider'

interface IDocumentUploadEditFieldProps {
    field: IField
    _id?: string
}

export const DocumentUploadEditField = ({
    field,
    _id,
}: IDocumentUploadEditFieldProps) => {
    const { getValue, updateValues } = useEditSectionContext()
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const [filesUploaded, setFilesUploaded] = useState<FileWithStatus[]>([])

    const disabled = useSignal(false)

    useEffect(() => {
        const val = getValue(field, _id)

        const initFieldValue = val === '' ? [] : tryParseJSON(val) // urls of files
        if (initFieldValue) {
            const uploadFiles: FileWithStatus[] = initFieldValue.map((f) => {
                const split = f.split('/')

                const fileName = split[split.length - 1]

                const noPrefixName = removePrefix(fileName)

                return {
                    file: { name: noPrefixName },
                    status: 'uploaded',
                    url: f,
                }
            })
            setFilesUploaded(uploadFiles)
        } else {
            setFilesUploaded([])
        }
    }, [field, _id, getValue])

    const onFilesChanged = async (files: FileWithStatus[]) => {
        //find new file if any
        const newFiles = files.filter((f) => f.status === 'uploading')
        if (newFiles.length > 0) {
            disabled.value = true
            const newFile = await useBoundStore
                .getState()
                .uploadMedia(newFiles[0].file, 'Document-Uploaded')
            if (newFile.status === 'success') {
                const newFileWithStatus = filesUploaded?.find(
                    (f) => f.file.name === newFiles[0].file.name
                )
                newFileWithStatus.status = 'uploaded'
                newFileWithStatus.url = newFile.data.source_url

                const stateFiles = [...filesUploaded]
                setFilesUploaded(stateFiles)

                const newValues = stateToValues(stateFiles)

                updateValues({ value: newValues, _id, field })
            } else {
                if (
                    (newFile.data as any).message ===
                    'Sorry, you are not allowed to upload this file type.'
                ) {
                    defaultAddSnack(
                        i18n.t('edit-itinerary.fields.errorWrongFileType'),
                        'error'
                    )
                }
                const newFileWithStatus: FileWithStatus = {
                    file: newFiles[0].file,
                    status: 'error',
                    url: newFile.data.source_url,
                }
                setFilesUploaded([...filesUploaded, newFileWithStatus])
            }
            disabled.value = false
        }
    }
    const onItemRemove = (item: FileWithStatus) => {
        const newFiles = filesUploaded.filter(
            (f) => f.file.name !== item.file.name
        )
        setFilesUploaded(newFiles)
        const newValues = stateToValues(newFiles)
        updateValues({ value: newValues, _id, field })
        clearDialog()
    }

    const stateToValues = (files: FileWithStatus[]) => {
        return files.map((f) => f.url)
    }
    const beforeRemove = (item: FileWithStatus) => {
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text={i18n.t('edit-itinerary.item.delete-attachment-text')}
                    header={i18n.t('edit-itinerary.item.delete-attachment')}
                    confirmText={i18n.t('general.delete')}
                    cancelText={i18n.t('general.cancel')}
                    confirmBtnStyle="destructiveBorderless"
                    onCancel={() => {
                        clearDialog()
                    }}
                    onConfirm={() => {
                        onItemRemove(item)
                    }}
                />
            ),
            backdropClosable: false,
        })
        return Promise.resolve(false)
    }

    return (
        <UploadFileField
            beforeItemRemove={beforeRemove}
            onFilesChanged={onFilesChanged}
            waitForUpload={true}
            values={filesUploaded}
            onItemRemove={onItemRemove}
            mobile={true}
            fieldLabel={editItineraryFieldsTranslation('Documents')}
            maxFiles={1}
            disabled={disabled.value}
            required={false}
            uploadButtonLabel={i18n.t('edit-itinerary.fields.upload-file')}
        />
    )
}
